import PropTypes from "prop-types";
import clsx from "clsx";
import { Link } from "react-router-dom";
import FooterCopyright from "../../components/footer/FooterCopyright";
import { useStoreInfo } from "../../hooks/storeInfoHooks/useStoreInfo";
import { t } from "i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import { getPhoneByCountry } from "../../helpers/countryHelper";

const FooterOne = ({
  backgroundColorClass,
  spaceTopClass,
  spaceBottomClass,
  spaceLeftClass,
  spaceRightClass,
  containerClass,
  extraFooterClass,
  sideMenu,
}) => {
  const { data: storeInfo, isLoading: isLoadingStoreInfo } = useStoreInfo();
  const { selectedCountry } = useSelector((state) => state.country);
  const countryPhone = getPhoneByCountry(storeInfo?.[0], selectedCountry);

  const activeSocialMedia =
    storeInfo?.[0]?.social_media?.filter((sm) => sm.status) || [];

  return (
    <footer
      className={clsx(
        "footer-area footer-modern rounded-top-5",
        backgroundColorClass,
        spaceTopClass,
        spaceBottomClass,
        extraFooterClass,
        spaceLeftClass,
        spaceRightClass
      )}
    >
      <div className={`${containerClass ? containerClass : "container"}`}>
        <div className="footer-modern-content">
          {/* Left Section - Logo & Description */}
          <div className="footer-modern-left">
            {isLoadingStoreInfo && (
              <>
                <Skeleton height={"50px"} width={"100px"} />
                <Skeleton height={"15px"} width={"200px"} count={2} />
              </>
            )}
            {!isLoadingStoreInfo && (
              <>
                <FooterCopyright
                  footerLogo={`${import.meta.env.VITE_REACT_APP_API_URL}/${
                    storeInfo?.[0]?.logo
                  }`}
                  spaceBottomClass="mb-20"
                  title={storeInfo?.[0]?.name}
                  description={storeInfo?.[0]?.description}
                />
              </>
            )}
          </div>

          {/* Middle Section - Navigation */}
          <div className="footer-modern-middle">
            {/* Column 1 - Our Services */}
            <div className="footer-modern-column">
              <h3 className="footer-modern-title">{t("shop") || "Shop"}</h3>
              <ul className="footer-modern-list">
                <li>
                  <Link to="/">{t("home") || "Home"}</Link>
                </li>
                <li>
                  <Link to="/shop">{t("shop") || "Shop"}</Link>
                </li>
                <li>
                  <Link to="/cart">{t("cart") || "Cart"}</Link>
                </li>
                <li>
                  <Link to="/wishlist">{t("wishlist") || "Wishlist"}</Link>
                </li>
              </ul>
            </div>

            {/* Column 2 - Important Links */}
            <div className="footer-modern-column">
              <h3 className="footer-modern-title">
                {t("important_links") || "Important Links"}
              </h3>
              <ul className="footer-modern-list">
                <li>
                  <Link to="/about">{t("about") || "About"}</Link>
                </li>
                <li>
                  <Link to="/services">{t("our_services") || "Services"}</Link>
                </li>
                <li>
                  <Link to="/contact">{t("contact") || "Contact"}</Link>
                </li>
                <li>
                  <Link to="/privacy-policy">
                    {t("privacy_policy") || "Privacy Policy"}
                  </Link>
                </li>
                <li>
                  <Link to="/shipping-info">
                    {t("shipping_info") || "Shipping Info"}
                  </Link>
                </li>
                <li>
                  <Link to="/return-policy">
                    {t("return_policy") || "Return Policy"}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3 - Resources */}
            <div className="footer-modern-column">
              <h3 className="footer-modern-title">
                {t("contact") || "Contact"}
              </h3>
              <ul className="footer-modern-list">
                <li>
                  {isLoadingStoreInfo && (
                    <Skeleton height={"15px"} width={"130px"} />
                  )}
                  {!isLoadingStoreInfo && (
                    <a
                      href={`tel:${countryPhone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {countryPhone}
                    </a>
                  )}
                </li>
                <li>
                  {isLoadingStoreInfo && (
                    <Skeleton height={"15px"} width={"130px"} />
                  )}
                  {!isLoadingStoreInfo && (
                    <a
                      href={`mailto:${storeInfo?.[0]?.email}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {storeInfo?.[0]?.email}
                    </a>
                  )}
                </li>
                <li>
                  {isLoadingStoreInfo && (
                    <Skeleton height={"15px"} width={"130px"} />
                  )}
                  {!isLoadingStoreInfo && (
                    <span>{storeInfo?.[0]?.address}</span>
                  )}
                </li>
              </ul>
            </div>
          </div>

          {/* Right Section - Social Icons */}
          <div className="footer-modern-right">
            <div className="footer-modern-social">
              {activeSocialMedia.map((social) => {
                return (
                  <a
                    key={social.name}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-modern-social-icon"
                    title={social.name}
                  >
                    <img
                      src={`${import.meta.env.VITE_REACT_APP_API_URL}/${
                        social.icon
                      }`}
                      alt={social.name}
                    />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-modern-bottom">
        <div className="container">
          <div className="footer-modern-copyright">
            <p className="mb-0">
              {isLoadingStoreInfo && (
                <Skeleton height={"15px"} width={"300px"} />
              )}
              {!isLoadingStoreInfo && storeInfo?.[0]?.footer_text}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

FooterOne.propTypes = {
  backgroundColorClass: PropTypes.string,
  containerClass: PropTypes.string,
  extraFooterClass: PropTypes.string,
  sideMenu: PropTypes.bool,
  spaceBottomClass: PropTypes.string,
  spaceTopClass: PropTypes.string,
  spaceLeftClass: PropTypes.string,
  spaceRightClass: PropTypes.string,
};

export default FooterOne;
